import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './OfficerDashboard.css';

function OfficerDashboard() {
  const { user, token } = useContext(AuthContext);
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedGrievance, setSelectedGrievance] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState({
    total: 0, pending: 0, satisfied: 0, notSatisfied: 0, miscellaneous: 0
  });

  const API_URL = process.env.REACT_APP_API_URL || 'https://backend.kpgmp.in/api';

  useEffect(() => { fetchGrievances(); }, []);
  useEffect(() => { if (grievances.length > 0) calculateStatsFromGrievances(grievances); }, [grievances]);

  const fetchGrievances = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/grievances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setGrievances(result.data || []);
      } else {
        setError(result.message || 'Failed to fetch grievances');
      }
    } catch (err) {
      console.error('Error fetching grievances:', err);
      setError('Failed to load grievances');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatsFromGrievances = (grievancesList) => {
    const statusCounts = grievancesList.reduce((acc, g) => {
      const status = g.status.toLowerCase().replace(/\s+/g, '');
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    setStats({
      total: grievancesList.length,
      pending: statusCounts['pending'] || 0,
      satisfied: statusCounts['satisfied'] || 0,
      notSatisfied: statusCounts['notsatisfied'] || 0,
      miscellaneous: statusCounts['miscellaneous'] || 0
    });
  };

  /* ── UPDATED: now also accepts enquiryOfficerName ── */
  const handleUpdateStatus = async (grievanceId, newStatus, remarks, enquiryOfficerName) => {
    try {
      const response = await fetch(`${API_URL}/grievances/${grievanceId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          remarks,
          enquiry_officer_name: enquiryOfficerName   // ← sent to backend
        })
      });

      const result = await response.json();
      if (result.success) {
        await fetchGrievances();
        setSelectedGrievance(null);
        alert('Grievance updated successfully!');
      } else {
        alert(result.message || 'Failed to update grievance');
      }
    } catch (err) {
      console.error('Error updating grievance:', err);
      alert('Failed to update grievance');
    }
  };

  /* ── Filters ── */
  const filteredByStatus = selectedStatus === 'all'
    ? grievances
    : grievances.filter(g => g.status === selectedStatus);

  const filteredBySearch = searchTerm
    ? filteredByStatus.filter(g =>
        g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.mobile.includes(searchTerm) ||
        g.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        g.current_ps.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (g.enquiry_officer_name || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : filteredByStatus;

  const filteredGrievances = (dateRange.startDate || dateRange.endDate)
    ? filteredBySearch.filter(g => {
        const gDate = new Date(g.first_receipt_date);
        if (dateRange.startDate && gDate < new Date(dateRange.startDate)) return false;
        if (dateRange.endDate   && gDate > new Date(dateRange.endDate))   return false;
        return true;
      })
    : filteredBySearch;

  /* ── Excel download ── */
  const downloadExcel = (filterType) => {
    try {
      let dataToDownload = [];
      let fileName = '';
      switch (filterType) {
        case 'all':         dataToDownload = grievances;                                                    fileName = `All_Grievances_${user?.current_ps || 'Officer'}`; break;
        case 'current':     dataToDownload = filteredGrievances;                                            fileName = `${selectedStatus === 'all' ? 'All' : selectedStatus}_Grievances_${user?.current_ps || 'Officer'}`; break;
        case 'pending':     dataToDownload = grievances.filter(g => g.status === 'Pending');                fileName = `Pending_Grievances_${user?.current_ps || 'Officer'}`; break;
        case 'satisfied':   dataToDownload = grievances.filter(g => g.status === 'Satisfied');              fileName = `Satisfied_Grievances_${user?.current_ps || 'Officer'}`; break;
        case 'not_satisfied': dataToDownload = grievances.filter(g => g.status === 'Not Satisfied');        fileName = `Not_Satisfied_Grievances_${user?.current_ps || 'Officer'}`; break;
        case 'miscellaneous': dataToDownload = grievances.filter(g => g.status === 'Miscellaneous');        fileName = `Miscellaneous_Grievances_${user?.current_ps || 'Officer'}`; break;
        default: dataToDownload = filteredGrievances;
      }

      if (dataToDownload.length === 0) { alert('No data to download for the selected filter.'); return; }

      const excelData = dataToDownload.map((g, index) => ({
        'Sr. No': index + 1,
        'Grievance ID': g.id,
        'Level': g.level,
        'Name': g.name,
        'Mobile': g.mobile,
        'Description': g.description,
        'Current PS': g.current_ps,
        'Status': g.status,
        'Enquiry Officer': g.enquiry_officer_name || '-',   // ← included in Excel
        'First Receipt Date': formatDate(g.first_receipt_date),
        'Last Marked Date': formatDate(g.last_marked_date),
        'Forwarded From Level': g.forwarded_from_level || '-',
        'Previous PS': g.previous_ps || '-',
        'Remarks': g.remarks || ''
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [8,12,8,20,12,40,30,15,25,18,18,18,30,40].map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, 'Grievances');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:]/g, '-');
      XLSX.writeFile(wb, `${fileName}_${timestamp}.xlsx`);
      alert(`Downloaded ${dataToDownload.length} grievances successfully!`);
    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Failed to download Excel file. Please try again.');
    }
  };

  const clearFilters = () => { setSearchTerm(''); setDateRange({ startDate: '', endDate: '' }); setSelectedStatus('all'); };
  const hasActiveFilters = searchTerm || dateRange.startDate || dateRange.endDate || selectedStatus !== 'all';

  const getStatusColor = (status) => ({
    'Pending': '#f59e0b', 'Satisfied': '#10b981',
    'Not Satisfied': '#ef4444', 'Miscellaneous': '#6366f1'
  }[status] || '#6b7280');

  const formatDate = (d) => {
    if (!d) return 'N/A';
    return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="officer-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your assigned grievances...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="officer-dashboard">

      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Officer Dashboard</h1>
          <p>Welcome, {user?.name || 'Officer'}</p>
          <span className="badge">{user?.current_ps}</span>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">📋</div>
          <div className="stat-content"><h3>{stats.total}</h3><p>Total Assigned</p></div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content"><h3>{stats.pending}</h3><p>Pending</p></div>
        </div>
        <div className="stat-card satisfied">
          <div className="stat-icon">✅</div>
          <div className="stat-content"><h3>{stats.satisfied}</h3><p>Satisfied</p></div>
        </div>
        <div className="stat-card not-satisfied">
          <div className="stat-icon">❌</div>
          <div className="stat-content"><h3>{stats.notSatisfied}</h3><p>Not Satisfied</p></div>
        </div>
      </div>

      {/* Download */}
      <div className="download-section">
        <div className="download-header">
          <span className="download-icon">📥</span>
          <span className="download-title">Download Excel Report</span>
        </div>
        <div className="download-buttons">
          <button className="download-btn" onClick={() => downloadExcel('all')}>Download All ({grievances.length})</button>
          <button className="download-btn" onClick={() => downloadExcel('current')}>Download Current View ({filteredGrievances.length})</button>
          <button className="download-btn" onClick={() => downloadExcel('pending')}>Download Pending ({stats.pending})</button>
          <button className="download-btn" onClick={() => downloadExcel('satisfied')}>Download Satisfied ({stats.satisfied})</button>
          <button className="download-btn" onClick={() => downloadExcel('not_satisfied')}>Download Not Satisfied ({stats.notSatisfied})</button>
          <button className="download-btn" onClick={() => downloadExcel('miscellaneous')}>Download Miscellaneous ({stats.miscellaneous})</button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="search-and-filter-section">
        <div className="search-bar">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, mobile, description, station, or enquiry officer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && <button className="clear-search" onClick={() => setSearchTerm('')} title="Clear search">✕</button>}
          <button className="toggle-filters-btn" onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? '▲ Hide Filters' : '▼ Show Filters'}
          </button>
        </div>

        {showFilters && (
          <div className="date-filter-section">
            <div className="date-filter">
              <label>Start Date:</label>
              <input type="date" value={dateRange.startDate} onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })} className="date-input" />
            </div>
            <div className="date-filter">
              <label>End Date:</label>
              <input type="date" value={dateRange.endDate} onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })} className="date-input" />
            </div>
            {hasActiveFilters && <button className="clear-all-filters-btn" onClick={clearFilters}>Clear All Filters</button>}
          </div>
        )}

        <div className="filter-tabs">
          <button className={selectedStatus === 'all'           ? 'active' : ''} onClick={() => setSelectedStatus('all')}>All ({grievances.length})</button>
          <button className={selectedStatus === 'Pending'       ? 'active' : ''} onClick={() => setSelectedStatus('Pending')}>Pending ({stats.pending})</button>
          <button className={selectedStatus === 'Satisfied'     ? 'active' : ''} onClick={() => setSelectedStatus('Satisfied')}>Satisfied ({stats.satisfied})</button>
          <button className={selectedStatus === 'Not Satisfied' ? 'active' : ''} onClick={() => setSelectedStatus('Not Satisfied')}>Not Satisfied ({stats.notSatisfied})</button>
          <button className={selectedStatus === 'Miscellaneous' ? 'active' : ''} onClick={() => setSelectedStatus('Miscellaneous')}>Miscellaneous ({stats.miscellaneous})</button>
        </div>

        <div className="filter-results">
          Showing {filteredGrievances.length} of {grievances.length} grievances
          {hasActiveFilters && <span className="filter-active"> (Filtered)</span>}
        </div>
      </div>

      {error && <div className="error-message"><span>⚠️</span> {error}</div>}

      {/* Grievance Cards */}
      <div className="grievances-container">
        {filteredGrievances.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon">📭</div>
            <h3>{searchTerm ? 'No Matching Results' : 'No Grievances Found'}</h3>
            <p>
              {searchTerm
                ? `No grievances match "${searchTerm}". Try a different search term.`
                : `You don't have any ${selectedStatus === 'all' ? '' : selectedStatus.toLowerCase()} grievances assigned.`
              }
            </p>
            {hasActiveFilters && <button className="btn-clear-search" onClick={clearFilters}>Clear All Filters</button>}
          </div>
        ) : (
          <div className="grievances-grid">
            {filteredGrievances.map((grievance) => (
              <div key={grievance.id} className="grievance-card">
                <div className="card-header">
                  <span className="level-badge" style={{ backgroundColor: getStatusColor(grievance.status) }}>
                    {grievance.level}
                  </span>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(grievance.status), opacity: 0.9 }}>
                    {grievance.status}
                  </span>
                </div>

                <div className="card-body">
                  <h3>{grievance.name}</h3>
                  <p className="description">{grievance.description}</p>

                  <div className="card-details">
                    <div className="detail-item">
                      <span className="detail-label">📞 Mobile:</span>
                      <span className="detail-value">{grievance.mobile}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">🏛️ Station:</span>
                      <span className="detail-value">{grievance.current_ps}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">📅 Received:</span>
                      <span className="detail-value">{formatDate(grievance.first_receipt_date)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">🕐 Last Updated:</span>
                      <span className="detail-value">{formatDate(grievance.last_marked_date)}</span>
                    </div>

                    {/* ── Enquiry Officer display on card ── */}
                    {grievance.enquiry_officer_name && (
                      <div className="detail-item enquiry-officer-item">
                        <span className="detail-label">🔍 Enquiry Officer:</span>
                        <span className="detail-value enquiry-officer-value">
                          {grievance.enquiry_officer_name}
                        </span>
                      </div>
                    )}

                    {(grievance.forwarded_from_level || grievance.previous_level || grievance.forwarded_from) && (
                      <div className="detail-item forwarded-info">
                        <span className="detail-label">↗️ Forwarded From:</span>
                        <span className="detail-value">
                          {grievance.forwarded_from_level || grievance.previous_level || grievance.forwarded_from || 'L-1'}
                          {(grievance.previous_ps || grievance.forwarded_ps) && ` - ${grievance.previous_ps || grievance.forwarded_ps}`}
                        </span>
                      </div>
                    )}
                  </div>

                  {grievance.remarks && (
                    <div className="remarks">
                      <strong>Remarks:</strong> {grievance.remarks}
                    </div>
                  )}
                </div>

                <div className="card-footer">
                  <button className="btn-view" onClick={() => setSelectedGrievance(grievance)}>
                    Update Status
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {selectedGrievance && (
        <UpdateStatusModal
          grievance={selectedGrievance}
          onClose={() => setSelectedGrievance(null)}
          onUpdate={handleUpdateStatus}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   UpdateStatusModal — with Enquiry Officer Name field added
───────────────────────────────────────────────────────────── */
function UpdateStatusModal({ grievance, onClose, onUpdate }) {
  const [status, setStatus]   = useState(grievance.status);
  const [remarks, setRemarks] = useState(grievance.remarks || '');

  /* Pre-fill with existing value so officer sees what's already saved */
  const [enquiryOfficerName, setEnquiryOfficerName] = useState(
    grievance.enquiry_officer_name || ''
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onUpdate(grievance.id, status, remarks, enquiryOfficerName);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <h2>Update Grievance Status</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Grievance info strip */}
          <div className="grievance-info">
            <h3>{grievance.name}</h3>
            <p><strong>Mobile:</strong> {grievance.mobile}</p>
            <p><strong>Description:</strong> {grievance.description}</p>
            {grievance.enquiry_officer_name && (
              <p className="current-enquiry-officer">
                <strong>🔍 Current Enquiry Officer:</strong> {grievance.enquiry_officer_name}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>

            {/* Status */}
            <div className="form-group">
              <label>Status *</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} required>
                <option value="Pending">Pending</option>
                <option value="Satisfied">Satisfied</option>
                <option value="Not Satisfied">Not Satisfied</option>
                <option value="Miscellaneous">Miscellaneous</option>
              </select>
            </div>

            {/* ── NEW: Enquiry Officer Name ── */}
            <div className="form-group">
              <label>Name of Enquiry Officer</label>
              <input
                type="text"
                value={enquiryOfficerName}
                onChange={(e) => setEnquiryOfficerName(e.target.value)}
                placeholder="e.g. Inspector Rajesh Kumar"
                className="enquiry-officer-input"
              />
              <small className="field-hint">Leave blank to keep existing value unchanged</small>
            </div>

            {/* Remarks */}
            <div className="form-group">
              <label>Remarks</label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                rows="4"
                placeholder="Add your remarks here..."
              />
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-submit">Update Status</button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default OfficerDashboard;